import { useEffect } from 'react';
import type { PortInfo, DiagramTransition, Line } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import type { DragDropState } from './useCanvasDragDropEvents.tsx';
import { useLatest } from './useLatest.tsx';
import { useSelectedTransitionInfo } from './useSelectedTransitionInfo.tsx';
import type { OnTransitionReroutingMovePayload } from './ReroutingTransitionValid.types.tsx';

export interface Options {
	ports: Record<string, PortInfo>;
	isImmutable: boolean;
	selectedItem: ItemReference | undefined;
	transitions: DiagramTransition[];
	dragDropState: DragDropState;
	onTransitionReroutingMove: (value: OnTransitionReroutingMovePayload) => void;
	onTransitionProposalMove: (startPort: string, line: Line) => void;
}

export const usePortDragAndDrop = ({
	ports,
	isImmutable,
	selectedItem,
	transitions,
	dragDropState,
	onTransitionReroutingMove,
	onTransitionProposalMove,
}: Options) => {
	const latestPortByStatusAndIndex = useLatest(ports);

	const selectedTransitionInfo = useSelectedTransitionInfo({
		transitions,
		selectedItem,
	});
	const latestSelectedTransitionInfo = useLatest(selectedTransitionInfo);

	useEffect(() => {
		if (isImmutable || !dragDropState.isDragging || dragDropState.dragSubject.type !== 'port') {
			// no port being dragged, nothing to do
			return;
		}

		const portBeingDragged = latestPortByStatusAndIndex.current[dragDropState.dragSubject.id];

		const latestSelectedTransition = latestSelectedTransitionInfo.current;

		const isSourcePortMoving = latestSelectedTransition?.sourcePortIndexKeys.some(
			(portKey) => portKey === portBeingDragged.key,
		);
		const isTargetPortMoving = latestSelectedTransition?.targetPortIndexKeys.some(
			(portKey) => portKey === portBeingDragged.key,
		);

		if (
			latestSelectedTransition &&
			latestSelectedTransition.transition.type !== 'global' &&
			!(latestSelectedTransition.transition.type === 'global-looped') &&
			(isSourcePortMoving || isTargetPortMoving)
		) {
			onTransitionReroutingMove({
				transition: latestSelectedTransition.transition,
				side: isSourcePortMoving ? 'source' : 'target',
				originalStatusId: portBeingDragged.statusId,
				originalPortKey: portBeingDragged.key,
				originalPortIndex: portBeingDragged.portIndex,
				currentPosition: dragDropState.currentPosition,
			});
		} else {
			onTransitionProposalMove(dragDropState.dragSubject.id, {
				start: portBeingDragged.position,
				end: dragDropState.currentPosition,
			});
		}
	}, [
		dragDropState,
		isImmutable,
		latestPortByStatusAndIndex,
		latestSelectedTransitionInfo,
		onTransitionProposalMove,
		onTransitionReroutingMove,
	]);
};
